import React, { useCallback, useEffect, useState } from 'react';
import axiosClient from '@/utils/client';
import { zodResolver } from '@hookform/resolvers/zod';
import { LoadingButton } from '@mui/lab';
import {
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  OutlinedInput,
  Stack,
  Switch,
  Typography,
  Box,
  Chip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import { Edit as EditIcon, Description, Image, PictureAsPdf, VideoFile, AudioFile, InsertDriveFile } from '@mui/icons-material';
import ProfessionalDialog from '@/components/common/professional-dialog';
import { useMutation } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import * as zod from 'zod';
import dayjs from 'dayjs';

import type { File } from '@/types/file';
import { AxiosError } from 'axios';

interface EditFileDialogProps {
  file: File | null;
  onClose: () => void;
  onEdit: () => void;
  open: boolean;
  onSuccess?: () => void;
}

interface FileApiResults {
  error?: string;
  message: string;
}

const schema = zod.object({
  visibility: zod.enum(['public', 'private']),
  name_original: zod.string().min(1, 'File name is required').max(255),
});

type Values = zod.infer<typeof schema>;

export function EditFileDialog({
  file,
  open,
  onClose,
  onEdit,
  onSuccess,
}: EditFileDialogProps): React.JSX.Element {
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  
  const defaultValues = {
    visibility: 'public' as const,
    name_original: '',
  } satisfies Values;

  const editFileRequest = useMutation({
    mutationFn: async (values: Values): Promise<FileApiResults> => {
      try {
        if (!file?.id) {
          throw new Error('No file selected for editing');
        }

        const response = await axiosClient.patch(`/api/v1/files/${file.id}`, {
          visibility: values.visibility,
          name_original: values.name_original,
        });

        return response.data;
      } catch (error) {
        if (error instanceof AxiosError && error.status === 403) {
          throw new Error('You are not authorized to edit this file');
        }
        console.log('Error', error);
        throw new Error('An error occurred while editing the file');
      }
    },
  });

  const {
    handleSubmit,
    reset,
    register,
    setValue,
    watch,
    formState: { errors },
  } = useForm<Values>({
    defaultValues,
    resolver: zodResolver(schema),
  });

  useEffect(() => {
    if (file) {
      setValue('visibility', file.visibility || 'public');
      setValue('name_original', file.name_original || file.name || '');
    }
  }, [file, setValue]);

  // Reset form when dialog closes
  useEffect(() => {
    if (!open) {
      reset(defaultValues);
    }
  }, [open, reset]);

  const handleEdit = useCallback(
    async (values: Values) => {
      await axiosClient.get('sanctum/csrf-cookie');

      try {
        const response = await editFileRequest.mutateAsync(values);

        if (response.error) {
          toast.error(response.error);
          return;
        }

        toast.success(response.message || 'File updated successfully');
        onEdit();
        reset();
      } catch (error) {
        if (error instanceof Error) {
          toast.error(error.message);
        } else {
          toast.error('An unknown error occurred');
        }
      }
    },
    [editFileRequest, onEdit, reset]
  );

  const handleDeleteClick = useCallback(() => {
    setDeleteConfirmOpen(true);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!file?.id) return;

    try {
      await axiosClient.delete(`/api/v1/files/${file.id}`);
      toast.success('File deleted successfully');
      onEdit(); // Refresh the parent component
      onClose();
      setDeleteConfirmOpen(false);
    } catch (error) {
      console.error('🚨 Error deleting file:', error);
      if (error instanceof Error) {
        toast.error(error.message);
      } else {
        toast.error('Failed to delete file');
      }
    }
  }, [file?.id, onEdit, onClose]);

  const handleDeleteCancel = useCallback(() => {
    setDeleteConfirmOpen(false);
  }, []);

  // Get file type icon
  const getFileTypeIcon = () => {
    if (!file) return <InsertDriveFile />;
    
    const mimeType = file.mime_type?.toLowerCase() || '';
    if (mimeType.includes('pdf')) return <PictureAsPdf />;
    if (mimeType.includes('image') || mimeType.includes('jpeg') || mimeType.includes('png')) return <Image />;
    if (mimeType.includes('video')) return <VideoFile />;
    if (mimeType.includes('audio')) return <AudioFile />;
    return <Description />;
  };

  // Get file type label
  const getFileTypeLabel = () => {
    if (!file) return 'Unknown';
    
    const mimeType = file.mime_type?.toLowerCase() || '';
    if (mimeType.includes('pdf')) return 'PDF Document';
    if (mimeType.includes('image') || mimeType.includes('jpeg') || mimeType.includes('png')) return 'Image';
    if (mimeType.includes('video')) return 'Video';
    if (mimeType.includes('audio')) return 'Audio';
    if (mimeType.includes('word') || mimeType.includes('document')) return 'Word Document';
    if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) return 'Spreadsheet';
    if (mimeType.includes('powerpoint') || mimeType.includes('presentation')) return 'Presentation';
    return 'Document';
  };

  // Format file size
  const formatFileSize = (bytes?: number) => {
    if (!bytes) return 'Unknown size';
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    if (bytes === 0) return '0 Bytes';
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${Math.round(bytes / Math.pow(1024, i) * 100) / 100  } ${  sizes[i]}`;
  };

  if (!file) return <></>;

  return (
    <>
      <ProfessionalDialog
      autoHeight
      fullWidth
      icon={<EditIcon />}
      maxWidth="sm"
      onClose={onClose}
      open={open}
      subtitle="Update file name and visibility"
      title="Edit File"
      variant="elevated"
    >
      <form onSubmit={handleSubmit(handleEdit)}>
        <Stack spacing={3}>
          {/* File Information Section */}
          <Box>
            <Typography gutterBottom sx={{ fontWeight: 600, mb: 2 }} variant="subtitle2">
              File Information
            </Typography>
            
            <Box sx={{ 
              p: 2, 
              border: '1px solid', 
              borderColor: 'divider', 
              borderRadius: 2,
              backgroundColor: 'background.paper'
            }}>
              <Stack spacing={2}>
                {/* File Header */}
                <Stack alignItems="center" direction="row" spacing={2}>
                  <Box sx={{ color: 'primary.main' }}>
                    {getFileTypeIcon()}
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography noWrap variant="h6">
                      {file.name}
                    </Typography>
                    <Chip 
                      color="primary" 
                      label={getFileTypeLabel()} 
                      size="small"
                      variant="outlined"
                    />
                  </Box>
                </Stack>

                <Divider />

                {/* File Details */}
                <Stack spacing={1.5}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography color="text.secondary" variant="body2">File Type:</Typography>
                    <Typography fontWeight={500} variant="body2">
                      {file.mime_type || 'Unknown'}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography color="text.secondary" variant="body2">File Size:</Typography>
                    <Typography fontWeight={500} variant="body2">
                      {file.size ? formatFileSize(file.size) : 'Unknown'}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography color="text.secondary" variant="body2">Upload Date:</Typography>
                    <Typography fontWeight={500} variant="body2">
                      {dayjs(file.created_at).format('MMM D, YYYY [at] h:mm A')}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography color="text.secondary" variant="body2">Current Visibility:</Typography>
                    <Chip 
                      color={file.visibility === 'private' ? 'default' : 'success'} 
                      label={file.visibility === 'private' ? 'Private' : 'Public'} 
                      size="small"
                      variant="outlined"
                    />
                  </Box>
                </Stack>
              </Stack>
            </Box>
          </Box>

          {/* File Name */}
          <Box>
            <Typography gutterBottom sx={{ fontWeight: 600, mb: 2 }} variant="subtitle2">
              File Name
            </Typography>
            <FormControl error={Boolean(errors.name_original)} fullWidth>
              <InputLabel htmlFor="name_original">Display Name</InputLabel>
              <OutlinedInput
                {...register('name_original')}
                fullWidth
                id="name_original"
                placeholder="Enter file name"
                type="text"
              />
              {errors.name_original && (
                <FormHelperText error>{errors.name_original.message}</FormHelperText>
              )}
            </FormControl>
          </Box>

          {/* Visibility Settings */}
          <Box>
            <Typography gutterBottom sx={{ fontWeight: 600, mb: 2 }} variant="subtitle2">
              Visibility Settings
            </Typography>
            
            <FormControl fullWidth>
              <FormControlLabel
                control={
                  <Switch
                    checked={watch('visibility') === 'private'}
                    onChange={(e) => {
                      setValue('visibility', e.target.checked ? 'private' : 'public');
                    }}
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography color="text.secondary" variant="body2">
                      {watch('visibility') === 'private' ? 'Private' : 'Public'}
                    </Typography>
                    <Typography color="text.secondary" variant="caption">
                      {watch('visibility') === 'private'
                        ? '(Only community managers and account admins can see this file)'
                        : '(All community members can see this file)'}
                    </Typography>
                  </Box>
                }
              />
            </FormControl>
          </Box>
          
          {/* Form Actions */}
          <Box sx={{ pt: 3 }}>
            {/* Info text */}
            <Typography 
              color="text.secondary" 
              sx={{ 
                display: 'block', 
                textAlign: 'center', 
                mb: 3,
                px: 2,
                py: 1,
                backgroundColor: 'action.hover',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider'
              }} 
              variant="caption"
            >
              Changes will be applied immediately
            </Typography>
            
            {/* Action buttons */}
            <Stack 
              direction="row" 
              justifyContent="center" 
              spacing={2}
              
            >
              {/* Cancel Button */}
              <LoadingButton
                disabled={editFileRequest.isPending}
                onClick={onClose}
                size="medium"
                sx={{
                  borderWidth: '2px',
                  '&:hover': {
                    borderWidth: '2px',
                    backgroundColor: 'action.hover'
                  }
                }}
                variant="outlined"
              >
                Cancel
              </LoadingButton>
              
              {/* Delete Button */}
              <LoadingButton
                color="error"
                disabled={editFileRequest.isPending}
                onClick={handleDeleteClick}
                size="medium"
                sx={{
                  borderWidth: '2px',
                  '&:hover': {
                    borderWidth: '2px',
                    backgroundColor: 'error.light',
                    color: 'error.contrastText'
                  }
                }}
                variant="outlined"
              >
                Delete File
              </LoadingButton>
              
              {/* Save Button */}
              <LoadingButton
                disabled={editFileRequest.isPending}
                loading={editFileRequest.isPending}
                size="medium"
                sx={{
                  background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                  boxShadow: (theme) => `0 4px 14px ${theme.palette.primary.main}40`,
                  '&:hover': {
                    background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                    boxShadow: (theme) => `0 6px 20px ${theme.palette.primary.main}60`
                  }
                }}
                type="submit"
                variant="contained"
              >
                Save Changes
              </LoadingButton>
            </Stack>
          </Box>
        </Stack>
      </form>
    </ProfessionalDialog>

    {/* Delete Confirmation Dialog */}
    <Dialog
      fullWidth
      maxWidth="xs"
      onClose={handleDeleteCancel}
      open={deleteConfirmOpen}
    >
      <DialogTitle sx={{ pb: 1 }}>
        Delete File
      </DialogTitle>
      <DialogContent>
        <Typography color="text.secondary" variant="body2">
          Are you sure you want to delete "{file?.name}"? This action cannot be undone.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ p: 2, pt: 1 }}>
        <Button onClick={handleDeleteCancel} variant="outlined">
          Cancel
        </Button>
        <Button 
          color="error" 
          disabled={editFileRequest.isPending} 
          onClick={handleDeleteConfirm}
          variant="contained"
        >
          Delete File
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
}
